import {
    isFunction,
    isObject,
    isEmpty,
    isArray,
    isPlainObject,
    fromPairs,
    values,
} from 'lodash';

export const SELECT_TYPE = 'select';
export const MULTISELECT_TYPE = 'multiselect';
export const NUMBER_TYPE = 'number';
export const TEXT_TYPE = 'text';
export const DATERANGE_TYPE = 'date_range';
export const TIMERANGE_TYPE = 'time_range';

const hidePriceFields = metaObj => {
    if (metaObj['select']) {
        return (
            metaObj['select']['price_category'] &&
            metaObj['select']['price_category'] === 'free'
        );
    }
};

const getAmenityPriceObj = amenity => [
    {
        id: amenity + 'price_category',
        type: SELECT_TYPE,
        label: 'Price: Category',
        key: 'price_category',
        groupBy: 'amenity_price',
        options: [
            {
                text: 'Free',
                value: 'free',
            },
            {
                text: 'Paid',
                value: 'paid',
            },
        ],
    },
    {
        id: amenity + 'amount',
        type: NUMBER_TYPE,
        label: 'Price: Amount',
        key: 'amount',
        parentGroup: 'amenity_price',
        groupBy: 'price_scheme',
        hideIf: value => hidePriceFields(value),
    },
    {
        id: amenity + 'currency',
        type: TEXT_TYPE,
        label: 'Price: Currency',
        key: 'currency',
        parentGroup: 'amenity_price',
        groupBy: 'price_scheme',
        hideIf: value => hidePriceFields(value),
    },
    {
        id: amenity + 'price_window',
        type: SELECT_TYPE,
        label: 'Price: Window',
        key: 'price_window',
        parentGroup: 'amenity_price',
        groupBy: 'price_scheme',
        hideIf: value => hidePriceFields(value),
        options: [
            {
                text: 'Per Stay',
                value: 'per_stay',
            },
            {
                text: 'Per Day',
                value: 'per_day',
            },
            {
                text: 'Per Hour',
                value: 'per_hour',
            },
        ],
    },
];

const getHoursObject = amenity => {
    return [
        {
            id: amenity + 'day_ranges',
            type: DATERANGE_TYPE,
            label: 'Hours: Day Ranges',
            key: 'day_ranges',
            groupBy: 'hours',
            options: [
                {
                    text: 'Start Date',
                    value: 'start_date',
                },
                {
                    text: 'End Date',
                    value: 'end_date',
                },
            ],
        },
        {
            id: amenity + 'time_ranges',
            type: TIMERANGE_TYPE,
            label: 'Hours: Time Ranges',
            key: 'time_ranges',
            groupBy: 'hours',
            options: [
                {
                    text: 'Start Time',
                    value: 'start_time',
                },
                {
                    text: 'End Time',
                    value: 'end_time',
                },
            ],
        },
        {
            id: amenity + 'days_of_week',
            type: MULTISELECT_TYPE,
            label: 'Hours: Days of Week',
            key: 'days_of_week',
            groupBy: 'hours',
            options: Array.from({ length: 7 }, (val, i) => i).map(item => ({
                value: item,
                text: `${item}`,
            })),
        },
    ];
};

const getAmenityOpenTimeObject = amenity => {
    return [
        {
            id: amenity + 'open_months',
            type: DATERANGE_TYPE,
            label: 'Open Months',
            key: 'open_months',
            options: [
                {
                    text: 'Start Date',
                    value: 'start_date',
                },
                {
                    text: 'End Date',
                    value: 'end_date',
                },
            ],
        },
        {
            id: amenity + 'open_times',
            type: TIMERANGE_TYPE,
            label: 'Hours: Open Times',
            key: 'open_times',
            options: [
                {
                    text: 'Start Time',
                    value: 'start_time',
                },
                {
                    text: 'End Time',
                    value: 'end_time',
                },
            ],
        },
    ];
};

export const getAmenityAdditionalMetaDataOptions = amenity => {
    //Returns array of available options
    amenity = amenity ? amenity.split('-')[0] : '';
    switch (amenity) {
        case 'bbq_area':
            return [
                {
                    id: amenity + 'bbq_area',
                    type: MULTISELECT_TYPE,
                    label: 'BBQ Grill',
                    key: 'bbq_grill_types',
                    options: [
                        { value: 'charcoal', text: 'Charcoal' },
                        { value: 'gas', text: 'Gas' },
                        { value: 'electric', text: 'Electric' },
                        { value: 'wood_burning', text: 'Wood' },
                    ],
                },
            ];
        case 'beachfront':
            return [
                {
                    id: amenity + 'beach_access',
                    type: SELECT_TYPE,
                    label: 'Beachfront',
                    key: 'is_beachfront',
                    options: [
                        { value: 'true', text: 'Yes' },
                        { value: 'false', text: 'No' },
                    ],
                },
                {
                    id: amenity + 'privacy',
                    type: SELECT_TYPE,
                    label: 'Privacy',
                    key: 'privacy',
                    options: [
                        { value: 'shared', text: 'Shared' },
                        { value: 'private', text: 'Private' },
                    ],
                },
            ];
        case 'patio_or_belcony':
            return [
                {
                    id: amenity + 'balcony',
                    type: SELECT_TYPE,
                    label: 'Privacy',
                    key: 'privacy',
                    options: [
                        {
                            text: 'Private',
                            value: 'private',
                        },
                        {
                            text: 'Shared',
                            value: 'shared',
                        },
                    ],
                },
            ];
        case 'crib':
            return [
                {
                    id: amenity + 'crib',
                    type: SELECT_TYPE,
                    label: 'Availability',
                    key: 'availabilitty',
                    options: [
                        { value: 'always_available', text: 'Always Available' },
                        {
                            value: 'available_upon_request',
                            text: 'Available Upon Request',
                        },
                    ],
                },
                {
                    id: amenity + 'privacy',
                    type: SELECT_TYPE,
                    label: 'Privacy',
                    key: 'privacy',
                    options: [
                        {
                            text: 'Private',
                            value: 'private',
                        },
                        {
                            text: 'Shared',
                            value: 'shared',
                        },
                    ],
                },
                {
                    id: amenity + 'size_type',
                    type: SELECT_TYPE,
                    label: 'Size',
                    key: 'size_type',
                    options: [
                        {
                            text: 'Standard',
                            value: 'STANDARD',
                        },
                        {
                            text: 'Mini',
                            value: 'MINI',
                        },
                    ],
                },
                {
                    id: amenity + 'crib_features',
                    type: MULTISELECT_TYPE,
                    label: 'Features',
                    key: 'crib_features',
                    options: [
                        {
                            text: 'Room Shades',
                            value: 'ROOM_SHADES',
                        },
                        {
                            text: 'Sheets Provided',
                            value: 'SHEETS_PROVIDED',
                        },
                    ],
                },
                ...getAmenityPriceObj(amenity),
            ];
        case 'tv':
            return [
                {
                    id: amenity + 'tv1',
                    type: NUMBER_TYPE,
                    label: 'Size in Inch',
                    key: 'size_in_inch',
                },
                {
                    id: amenity + 'tv2',
                    type: SELECT_TYPE,
                    label: 'Quality',
                    key: 'quality',
                    options: [
                        {
                            text: 'Standard',
                            value: 'standard',
                        },
                        {
                            text: 'HD',
                            value: 'hd',
                        },
                    ],
                },
                {
                    id: amenity + 'tv3',
                    type: MULTISELECT_TYPE,
                    label: 'Services',
                    key: 'services',
                    options: [
                        {
                            text: 'Disney Plus',
                            value: 'disney_plus',
                        },
                        {
                            text: 'Netflix',
                            value: 'netflix',
                        },
                        {
                            text: 'Hulu',
                            value: 'hulu',
                        },
                        {
                            text: 'Amazon',
                            value: 'amazon',
                        },
                        {
                            text: 'Apple TV',
                            value: 'apple_tv',
                        },
                        {
                            text: 'Chromecast',
                            value: 'chromecast',
                        },
                        {
                            text: 'Fire TV',
                            value: 'fire_tv',
                        },

                        {
                            text: 'HBO GO',
                            value: 'hbo_go',
                        },
                        {
                            text: 'Premium Cable',
                            value: 'premium_cable',
                        },
                        {
                            text: 'Roku',
                            value: 'roku',
                        },
                        {
                            text: 'Standard Cable',
                            value: 'standard_cable',
                        },
                    ],
                },
            ];
        // case 'kitchen':  Return nothing
        //     return [
        //         {
        //             id: amenity + 'kitchen',
        //             type: SELECT_TYPE,
        //             label: 'Kitchen',
        //             key: 'kitchen_type',
        //             options: [
        //                 {
        //                     text: 'Full Kitchen',
        //                     value: 'full_kitchen',
        //                 },
        //                 {
        //                     text: 'Kitchenette',
        //                     value: 'kitchenette',
        //                 },
        //             ],
        //         },
        //     ];
        case 'ac':
            return [
                {
                    id: amenity + 'ac',
                    type: MULTISELECT_TYPE,
                    label: 'Air Condition',
                    key: 'ac_options',
                    options: [
                        {
                            text: 'Central',
                            value: 'central',
                        },
                        {
                            text: 'Portable',
                            value: 'portable',
                        },
                        {
                            text: 'Floor Cooling',
                            value: 'floor_cooling',
                        },
                        {
                            text: 'Window AC Unit',
                            value: 'window_ac_unit',
                        },
                    ],
                },
            ];
        // case 'bed_linens': Return nothing
        //     return [
        //         {
        //             id: amenity + 'bed_linens',
        //             type: TEXT_TYPE,
        //             label: 'Brand',
        //             key: 'brand',
        //         },
        //         {
        //             id: amenity + 'bed_linens_material',
        //             type: SELECT_TYPE,
        //             label: 'Material',
        //             key: 'bedLinensMaterial',
        //             options: [
        //                 {
        //                     text: 'Cotton',
        //                     value: 'cotton',
        //                 },
        //                 {
        //                     text: 'Egyptian Cotton',
        //                     value: 'egyption_cotton',
        //                 },
        //                 {
        //                     text: 'Flannel',
        //                     value: 'flannel',
        //                 },
        //                 {
        //                     text: 'Silk',
        //                     value: 'silk',
        //                 },
        //             ],
        //         },
        //     ];
        case 'childrens_books_and_toys':
            return [
                {
                    id: amenity + 'childrens_books_and_toys',
                    type: MULTISELECT_TYPE,
                    label: 'Book And Toys',
                    key: 'ageGroups',
                    options: [
                        {
                            text: '0-2 years',
                            value: 'zero_to_two_years_old',
                        },
                        {
                            text: '2-5 years',
                            value: 'two_to_five_years_old',
                        },
                        {
                            text: '5-10 years',
                            value: 'five_to_ten_years_old',
                        },
                        {
                            text: '10+ years',
                            value: 'ten_and_older_years_old',
                        },
                    ],
                },
                // { Return nothing
                //     id: amenity + 'toys_types',
                //     type: MULTISELECT_TYPE,
                //     label: 'Toys Types',
                //     key: 'toys_types',
                //     options: [
                //         {
                //             text: 'Indoor Slide',
                //             value: 'INDOOR_SLIDE',
                //         },
                //         {
                //             text: 'Indoor Tent',
                //             value: 'INDOOR_TENT',
                //         },
                //     ],
                // },
            ];

        // case 'breakfast': Return Nothing
        //     return [
        //         {
        //             id: amenity + 'type',
        //             type: SELECT_TYPE,
        //             label: 'Breakfast Type',
        //             key: 'breakfast_type',
        //             options: [
        //                 {
        //                     text: 'Continental',
        //                     value: 'continental',
        //                 },
        //                 {
        //                     text: 'Hot',
        //                     value: 'hot',
        //                 },
        //                 {
        //                     text: 'Buffet',
        //                     value: 'buffet',
        //                 },
        //                 {
        //                     text: 'Cooked To Order',
        //                     value: 'cooked_to_order',
        //                 },
        //                 {
        //                     text: 'Self Serve',
        //                     value: 'self_serve',
        //                 },
        //                 {
        //                     text: 'English Breakfast',
        //                     value: 'english_breakfast',
        //                 },
        //                 {
        //                     text: 'Local Cuisine Breakfast',
        //                     value: 'local_cuisine_breakfast',
        //                 },
        //             ],
        //         },
        //         // ...getHoursObject(amenity),
        //         {
        //             id: amenity + 'amount',
        //             type: NUMBER_TYPE,
        //             label: 'Price: Amount',
        //             key: 'amount',
        //             groupBy: 'price',
        //         },
        //         {
        //             id: amenity + 'currency',
        //             type: TEXT_TYPE,
        //             label: 'Price: Currency',
        //             key: 'currency',
        //             groupBy: 'price',
        //         },
        //         {
        //             id: amenity + 'price_type',
        //             type: SELECT_TYPE,
        //             label: 'Price: Type',
        //             key: 'price_type',
        //             groupBy: 'price',

        //             options: [
        //                 {
        //                     text: 'Per Stay',
        //                     value: 'per_stay',
        //                 },
        //                 {
        //                     text: 'Per Night',
        //                     value: 'per_night',
        //                 },
        //                 {
        //                     text: 'Per Persont Per Stay',
        //                     value: 'per_person_per_stay',
        //                 },
        //                 {
        //                     text: 'Per Person Per Night',
        //                     value: 'per_person_per_night',
        //                 },
        //             ],
        //         },
        //         {
        //             id: amenity + 'price_availability',
        //             type: SELECT_TYPE,
        //             label: 'Price: Availability',
        //             key: 'price_availability',
        //             groupBy: 'price',
        //             options: [
        //                 {
        //                     text: 'Available',
        //                     value: 'available',
        //                 },
        //                 {
        //                     text: 'Unavailable',
        //                     value: 'unavailable',
        //                 },
        //             ],
        //         },
        //     ];
        /* case 'carbon_monoxide_detector': Return 500
            return [
                {
                    id: amenity + 'carbon_monoxide_detector',
                    type: SELECT_TYPE,
                    label: 'Carbon Monoxide Detector',
                    key: 'confirmed_safe',
                    options: [
                        {
                            text: 'Yes',
                            value: 'true',
                        },
                        {
                            text: 'No',
                            value: 'false',
                        },
                    ],
                },
            ];
            */
        case 'coffee_maker':
            return [
                {
                    id: amenity + 'coffee_maker',
                    type: MULTISELECT_TYPE,
                    label: 'Coffee Maker',
                    key: 'coffee_maker_types',
                    options: [
                        {
                            text: 'Drip Cofee',
                            value: 'drip_coffee',
                        },
                        {
                            text: 'French Press',
                            value: 'french_press',
                        },
                        {
                            text: 'Espresso Machine',
                            value: 'espresso_machine',
                        },
                        {
                            text: 'Keurig',
                            value: 'keurig',
                        },
                        {
                            text: 'Nespresso',
                            value: 'nespresso',
                        },
                        {
                            text: 'Pour Over Cofee',
                            value: 'pour_over_coffee',
                        },
                    ],
                },
            ];
        case 'shampoo':
            return [
                {
                    id: amenity + 'shampoo',
                    type: TEXT_TYPE,
                    label: 'Brand',
                    key: 'brand',
                },
            ];
        // case 'ev_charger': Return nothing
        //     return [
        //         {
        //             id: amenity + 'ev_charger',
        //             type: SELECT_TYPE,
        //             label: 'EV Charger',
        //             key: 'level',
        //             options: [
        //                 {
        //                     text: 'Level 1',
        //                     value: 'level_1',
        //                 },
        //                 {
        //                     text: 'Level 2',
        //                     value: 'level_2',
        //                 },
        //                 {
        //                     text: 'Level 3',
        //                     value: 'level_3',
        //                 },
        //             ],
        //         },
        //     ];
        case 'fireplace':
            return [
                {
                    id: amenity + 'fireplace',
                    type: MULTISELECT_TYPE,
                    label: 'Fireplace',
                    key: 'fireplace_types',
                    options: [
                        {
                            text: 'Ethanol',
                            value: 'ethanol',
                        },
                        {
                            text: 'Electric',
                            value: 'electric',
                        },
                        {
                            text: 'Gas',
                            value: 'gas',
                        },
                        {
                            text: 'Pellet Stove',
                            value: 'pellet_stove',
                        },
                        {
                            text: 'Wood Burning',
                            value: 'wood_burning',
                        },
                    ],
                },
            ];
        case 'refrigerator':
            return [
                {
                    id: amenity + 'fridge_type',
                    type: SELECT_TYPE,
                    label: 'Frighe Type',
                    key: 'fridge_type',
                    options: [
                        {
                            text: 'Fridge',
                            value: 'fridge',
                        },
                        {
                            text: 'Mini Fridge',
                            value: 'mini_fridge',
                        },
                    ],
                },
                {
                    id: amenity + 'brand',
                    type: TEXT_TYPE,
                    label: 'Frighe Brand',
                    key: 'brand',
                },
            ];
        case 'game_console':
            return [
                {
                    id: amenity + 'game_console_types',
                    type: MULTISELECT_TYPE,
                    label: 'Game Console',
                    key: 'game_console_types',
                    options: [
                        {
                            text: 'Nintendo Wii',
                            value: 'nintendo_wii',
                        },
                        {
                            text: 'Nintendo Switch',
                            value: 'nintendo_switch',
                        },
                        {
                            text: 'Nintendo WII U',
                            value: 'nintendo_wii_u',
                        },
                        {
                            text: 'PS2',
                            value: 'ps2',
                        },
                        {
                            text: 'PS3',
                            value: 'ps3',
                        },
                        {
                            text: 'PS4',
                            value: 'ps4',
                        },
                        {
                            text: 'PS5',
                            value: 'ps5',
                        },
                        {
                            text: 'Xbox 360',
                            value: 'xbox_360',
                        },
                        {
                            text: 'Xbox one',
                            value: 'xbox_one',
                        },
                        {
                            text: 'Xbox Series X',
                            value: 'xbox_series_x',
                        },
                    ],
                },
            ];
        case 'garden_or_backyard':
            return [
                {
                    id: amenity + 'privacy',
                    type: SELECT_TYPE,
                    label: 'Privacy',
                    key: 'privacy',
                    options: [
                        {
                            text: 'Private',
                            value: 'private',
                        },
                        {
                            text: 'Shared',
                            value: 'shared',
                        },
                    ],
                },
                {
                    id: amenity + 'fenced',
                    type: SELECT_TYPE,
                    label: 'Fenced',
                    key: 'fenced',
                    options: [
                        {
                            text: 'Yes',
                            value: 'true',
                        },
                        {
                            text: 'No',
                            value: 'false',
                        },
                    ],
                },
            ];
        case 'gym':
            return [
                {
                    id: amenity + 'privacy',
                    type: SELECT_TYPE,
                    label: 'Privacy',
                    key: 'privacy',
                    options: [
                        {
                            text: 'Private',
                            value: 'private',
                        },
                        {
                            text: 'Shared',
                            value: 'shared',
                        },
                    ],
                },
                {
                    id: amenity + 'gym_location',
                    type: SELECT_TYPE,
                    label: 'Location',
                    key: 'gym_location',
                    options: [
                        {
                            text: 'In Building',
                            value: 'in_building',
                        },
                        {
                            text: 'Near By',
                            value: 'nearby',
                        },
                    ],
                },
            ];
        case 'heating':
            return [
                {
                    id: amenity + 'heating_options',
                    type: MULTISELECT_TYPE,
                    label: 'Heating Options',
                    key: 'heating_options',
                    options: [
                        {
                            text: 'Radiant',
                            value: 'radiant',
                        },
                        {
                            text: 'Central',
                            value: 'central',
                        },
                        {
                            text: 'Portable Heater',
                            value: 'portable_heater',
                        },
                        {
                            text: 'Split type ductless',
                            value: 'SPLIT_TYPE_DUCTLESS',
                        },
                    ],
                },
            ];
        case 'jacuzzi':
            return [
                {
                    id: amenity + 'privacy',
                    type: SELECT_TYPE,
                    label: 'Privacy',
                    key: 'privacy',
                    options: [
                        {
                            text: 'Private',
                            value: 'private',
                        },
                        {
                            text: 'Shared',
                            value: 'shared',
                        },
                    ],
                },
                // { Returns nothing
                //     id: amenity + 'number_of_people',
                //     type: NUMBER_TYPE,
                //     label: 'Number of people',
                //     key: 'number_of_people',
                // },
                // {
                //     id: amenity + 'seasonality',
                //     type: SELECT_TYPE,
                //     label: 'Seasonality',
                //     key: 'seasonality',
                //     options: [
                //         {
                //             text: 'Seasonal',
                //             value: 'seasonal',
                //         },
                //         {
                //             text: 'All Year',
                //             value: 'all_year',
                //         },
                //     ],
                // },
                // {
                //     id: amenity + 'open_hours_availability',
                //     type: SELECT_TYPE,
                //     label: 'Open Hours Availability',
                //     key: 'open_hours_availability',
                //     options: [
                //         {
                //             text: 'All Day',
                //             value: 'all_day',
                //         },
                //         {
                //             text: 'Specific Times',
                //             value: 'specific_times',
                //         },
                //     ],
                // },
                // ...getAmenityOpenTimeObject(amenity),
            ];
        case 'dryer':
        case 'washer':
            return [
                {
                    id: amenity + 'location',
                    type: SELECT_TYPE,
                    label: 'Location',
                    key: 'location',
                    options: [
                        {
                            text: 'In Building',
                            value: 'in_building',
                        },
                        {
                            text: 'In Unit',
                            value: 'in_unit',
                        },
                    ],
                },
                ...getAmenityPriceObj(amenity),
            ];
        case 'oven':
            return [
                {
                    id: amenity + 'brand',
                    type: TEXT_TYPE,
                    label: 'Brand',
                    key: 'brand',
                },
                {
                    id: amenity + 'material',
                    type: MULTISELECT_TYPE,
                    label: 'Material',
                    key: 'oven_materials',
                    options: [
                        {
                            text: 'Stainless Steel',
                            value: 'stainless_steel',
                        },
                    ],
                },
            ];

        case 'pack_n_play_travel_crib':
            return [
                {
                    id: amenity + 'availabilitty',
                    type: SELECT_TYPE,
                    label: 'Availabilitty',
                    key: 'availabilitty',
                    options: [
                        {
                            text: 'Always Available Steel',
                            value: 'always_available',
                        },
                        {
                            text: 'Available Upon Request',
                            value: 'available_upon_request',
                        },
                    ],
                },
                {
                    id: amenity + 'pack_n_play_travel_crib_features',
                    type: MULTISELECT_TYPE,
                    label: 'Features',
                    key: 'pack_n_play_travel_crib_features',
                    options: [
                        {
                            text: 'Room Shades',
                            value: 'ROOM_SHADES',
                        },
                        {
                            text: 'Sheets Provided',
                            value: 'SHEETS_PROVIDED',
                        },
                    ],
                },
                {
                    id: amenity + 'crib_type',
                    type: SELECT_TYPE,
                    label: 'Type',
                    key: 'crib_type',
                    options: [
                        {
                            text: 'Pack and Play',
                            value: 'PACK_N_PLAY',
                        },
                        {
                            text: 'Travel Crib',
                            value: 'TRAVEL_CRIB',
                        },
                    ],
                },
                ...getAmenityPriceObj(amenity),
            ];
        // case 'street_parking':
        case 'free_parking':
            return [
                {
                    id: amenity + 'number_of_spaces',
                    type: NUMBER_TYPE,
                    label: 'Number of Spaces',
                    key: 'number_of_spaces',
                },
                {
                    id: amenity + 'parking_type',
                    type: SELECT_TYPE,
                    label: 'Parking Type',
                    key: 'parking_type',
                    options: [
                        {
                            text: 'Driveway',
                            value: 'driveway',
                        },
                        {
                            text: 'Residential Garage',
                            value: 'residential_garage',
                        },
                        {
                            text: 'Parking Garage',
                            value: 'parking_garage',
                        },
                        {
                            text: 'Carport',
                            value: 'carport',
                        },
                        {
                            text: 'Parking Lot',
                            value: 'parking_lot',
                        },
                        {
                            text: 'Valet Parking',
                            value: 'valet_parking',
                        },
                    ],
                },
                ...getAmenityPriceObj(amenity),
            ];
        case 'paid_parking':
        case 'street_parking':
        case 'paid_parking_on_premises':
            return [
                {
                    id: amenity + 'number_of_spaces',
                    type: NUMBER_TYPE,
                    label: 'Number of Spaces',
                    key: 'number_of_spaces',
                },
                {
                    id: amenity + 'parking_type',
                    type: SELECT_TYPE,
                    label: 'Parking Type',
                    key: 'parking_type',
                    options: [
                        {
                            text: 'Driveway',
                            value: 'driveway',
                        },
                        {
                            text: 'Residential Garage',
                            value: 'residential_garage',
                        },
                        {
                            text: 'Parking Garage',
                            value: 'parking_garage',
                        },
                        {
                            text: 'Carport',
                            value: 'carport',
                        },
                        {
                            text: 'Parking Lot',
                            value: 'parking_lot',
                        },
                        {
                            text: 'Valet Parking',
                            value: 'valet_parking',
                        },
                    ],
                },
            ];
        case 'pool':
            return [
                {
                    id: amenity + 'privacy',
                    type: SELECT_TYPE,
                    label: 'Privacy',
                    key: 'privacy',
                    options: [
                        {
                            text: 'Private',
                            value: 'private',
                        },
                        {
                            text: 'Shared',
                            value: 'shared',
                        },
                    ],
                },
                {
                    id: amenity + 'access_type',
                    type: SELECT_TYPE,
                    label: 'Access Type',
                    key: 'access_type',
                    options: [
                        {
                            text: 'Indoor',
                            value: 'indoor',
                        },
                        {
                            text: 'Outdoor',
                            value: 'outdoor',
                        },
                    ],
                },
                // {
                //     id: amenity + 'seasonality',
                //     type: SELECT_TYPE,
                //     label: 'Seasonality',
                //     key: 'seasonality',
                //     options: [
                //         {
                //             text: 'Seasonal',
                //             value: 'seasonal',
                //         },
                //         {
                //             text: 'All Year',
                //             value: 'all_year',
                //         },
                //     ],
                // },
                {
                    id: amenity + 'features',
                    type: MULTISELECT_TYPE,
                    label: 'Features',
                    key: 'features',
                    options: [
                        {
                            text: 'Heated',
                            value: 'heated',
                        },
                        {
                            text: 'Infinity',
                            value: 'infinity',
                        },
                        {
                            text: 'Lap Pool',
                            value: 'lap_pool',
                        },
                        {
                            text: 'Olympic Sized',
                            value: 'olympic_sized',
                        },
                        {
                            text: 'Rooftop',
                            value: 'rooftop',
                        },
                        {
                            text: 'Salt Water',
                            value: 'saltwater',
                        },
                    ],
                },
                // ...getAmenityOpenTimeObject(amenity),
            ];

        case 'ski_in_ski_out':
            return [
                {
                    id: amenity + 'ski_location',
                    type: SELECT_TYPE,
                    label: 'Ski Location',
                    key: 'ski_location',
                    options: [
                        {
                            text: 'Free Shuttle',
                            value: 'free_shuttle',
                        },
                        {
                            text: 'Near Lifts',
                            value: 'near_lifts',
                        },
                        {
                            text: 'On Ski Run',
                            value: 'on_ski_run',
                        },
                    ],
                },
            ];
        case 'stove':
            return [
                {
                    id: amenity + 'brand',
                    type: TEXT_TYPE,
                    label: 'Brand',
                    key: 'brand',
                },
                {
                    id: amenity + 'stove_type',
                    type: SELECT_TYPE,
                    label: 'Stove Type',
                    key: 'stove_type',
                    options: [
                        {
                            text: 'Free Shuttle',
                            value: 'electric',
                        },
                        {
                            text: 'Gas',
                            value: 'gas',
                        },
                        {
                            text: 'Induction',
                            value: 'induction',
                        },
                    ],
                },
                {
                    id: amenity + 'stove_materials',
                    type: MULTISELECT_TYPE,
                    label: 'Material',
                    key: 'stove_materials',
                    options: [
                        {
                            text: 'Stainless Steel',
                            value: 'stainless_steel',
                        },
                    ],
                },
            ];
        // case 'wireless_internet': Return nothing
        //     return [
        //         {
        //             id: amenity + 'speed_in_mbps',
        //             type: NUMBER_TYPE,
        //             label: 'Speed in Mbps',
        //             key: 'speed_in_mbps',
        //         },
        //     ];
        case 'high_chair':
            return [
                {
                    id: amenity + 'availability',
                    type: SELECT_TYPE,
                    label: 'Availability',
                    key: 'availability',
                    options: [
                        {
                            value: 'always_available'.toUpperCase(),
                            text: 'Always Available',
                        },
                        {
                            value: 'available_upon_request'.toUpperCase(),
                            text: 'Available Upon Request',
                        },
                    ],
                },
                {
                    id: amenity + 'high_chair_type',
                    type: SELECT_TYPE,
                    label: 'High Chair Type',
                    key: 'high_chair_type',
                    options: [
                        {
                            value: 'STANDALONE',
                            text: 'Standalone',
                        },
                        {
                            value: 'FOLDING_OR_CONVERTIBLE',
                            text: 'Folding of convertible',
                        },
                        {
                            value: 'BOOSTER_SEAT',
                            text: 'Booster seat',
                        },
                        {
                            value: 'CLAMP_TABLE_SEAT',
                            text: 'Clamp table seat',
                        },
                    ],
                },
                {
                    id: amenity + 'high_chair_features',
                    type: MULTISELECT_TYPE,
                    label: 'High Chair Feature',
                    key: 'high_chair_features',
                    options: [
                        {
                            value: 'PADDING',
                            text: 'Padding',
                        },
                        {
                            value: 'STRAPS_OR_HARNESS',
                            text: 'Straps or harness',
                        },
                        {
                            value: 'FOOD_TRAY',
                            text: 'Food tray',
                        },
                    ],
                },
                ...getAmenityPriceObj(amenity),
            ];
        case 'laptop_friendly_workspace':
            return [
                {
                    id: amenity + 'workspace_setup_types',
                    type: MULTISELECT_TYPE,
                    label: 'Workspace setup Types',
                    key: 'workspace_setup_types',
                    options: [
                        {
                            text: 'Desk',
                            value: 'desk',
                        },
                        {
                            text: 'Monitor',
                            value: 'monitor',
                        },
                        {
                            text: 'Office Chair',
                            value: 'office_chair',
                        },
                        {
                            text: 'Table',
                            value: 'table',
                        },
                        {
                            text: 'Ergonomic Chair',
                            value: 'ergonomic_chair',
                        },
                        {
                            text: 'Laptop Stand',
                            value: 'laptop_stand',
                        },
                        {
                            text: 'Surge Protector',
                            value: 'surge_protector',
                        },
                        {
                            text: 'Power Strip',
                            value: 'POWER_STRIP',
                        },
                        {
                            text: 'Printer',
                            value: 'PRINTER',
                        },
                    ],
                },
            ];
        case 'body_soap':
            return [
                {
                    id: amenity + 'brand',
                    type: TEXT_TYPE,
                    label: 'Brand',
                    key: 'brand',
                },
            ];
        case 'conditioner':
            return [
                {
                    id: amenity + 'brand',
                    type: TEXT_TYPE,
                    label: 'Brand',
                    key: 'brand',
                },
            ];
        case 'wardrobe_or_closet':
            return [
                {
                    id: amenity + 'clothing_storage_types',
                    type: MULTISELECT_TYPE,
                    label: 'Clothing Storage Types',
                    key: 'clothing_storage_types',
                    options: [
                        {
                            text: 'Closet',
                            value: 'closet',
                        },
                        {
                            text: 'Dresser',
                            value: 'dresser',
                        },
                        {
                            text: 'Walk-in Closet',
                            value: 'walk_in_closet',
                        },
                        {
                            text: 'Wardrobe',
                            value: 'wardrobe',
                        },
                    ],
                },
            ];
        case 'exercise_equipment':
            return [
                {
                    id: amenity + 'equipment_types',
                    type: MULTISELECT_TYPE,
                    label: 'Equipment Types',
                    key: 'equipment_types',
                    options: [
                        {
                            text: 'Elliptical',
                            value: 'elliptical',
                        },
                        {
                            text: 'Free Weights',
                            value: 'free_weights',
                        },
                        {
                            text: 'Stationary Bike',
                            value: 'stationary_bike',
                        },
                        {
                            text: 'Treadmill',
                            value: 'treadmill',
                        },
                        {
                            text: 'Yoga Mat',
                            value: 'yoga_mat',
                        },
                        {
                            text: 'Rowing',
                            value: 'rowing',
                        },
                        {
                            text: 'Workout Bench',
                            value: 'workout_bench',
                        },
                    ],
                },
            ];
        case 'piano':
            return [
                {
                    id: amenity + 'piano_type',
                    type: SELECT_TYPE,
                    label: 'Piano Type',
                    key: 'piano_type',
                    options: [
                        {
                            text: 'Grand',
                            value: 'grand',
                        },
                        {
                            text: 'Keyboard',
                            value: 'keyboard',
                        },
                        {
                            text: 'Upright',
                            value: 'upright',
                        },
                    ],
                },
                {
                    id: amenity + 'brand',
                    type: TEXT_TYPE,
                    label: 'Brand',
                    key: 'brand',
                },
            ];
        case 'sound_system':
            return [
                {
                    id: amenity + 'connectivity',
                    type: MULTISELECT_TYPE,
                    label: 'Connectivity',
                    key: 'connectivity',
                    options: [
                        {
                            text: 'Aux',
                            value: 'aux',
                        },
                        {
                            text: 'Bluetooth',
                            value: 'bluetooth',
                        },
                    ],
                },
                {
                    id: amenity + 'brand',
                    type: TEXT_TYPE,
                    label: 'Brand',
                    key: 'brand',
                },
            ];
        case 'boat_slip':
            return [
                {
                    id: amenity + 'privacy',
                    type: SELECT_TYPE,
                    label: 'Privacy',
                    key: 'privacy',
                    options: [
                        {
                            text: 'Private',
                            value: 'private',
                        },
                        {
                            text: 'Shared',
                            value: 'shared',
                        },
                    ],
                },
            ];
        case 'alfresco_dining':
            return [
                {
                    id: amenity + 'number_of_spaces',
                    type: NUMBER_TYPE,
                    label: 'Number of Spaces',
                    key: 'number_of_spaces',
                },
            ];
        case 'outdoor_kitchen':
            return [
                {
                    id: amenity + 'privacy',
                    type: SELECT_TYPE,
                    label: 'Privacy',
                    key: 'privacy',
                    options: [
                        {
                            text: 'Private',
                            value: 'private',
                        },
                        {
                            text: 'Shared',
                            value: 'shared',
                        },
                    ],
                },
                {
                    id: amenity + 'outdoor_kitchen_features',
                    type: MULTISELECT_TYPE,
                    label: 'Outdoor Kitchen Features',
                    key: 'outdoor_kitchen_features',
                    options: [
                        {
                            text: 'Oven',
                            value: 'oven',
                        },
                        {
                            text: 'Sink',
                            value: 'sink',
                        },
                    ],
                },
            ];
        case 'dining_table':
            return [
                {
                    id: amenity + 'number_of_spaces',
                    type: NUMBER_TYPE,
                    label: 'Number of Spaces',
                    key: 'number_of_spaces',
                },
            ];
        case 'sauna':
            return [
                {
                    id: amenity + 'privacy',
                    type: SELECT_TYPE,
                    label: 'Privacy',
                    key: 'privacy',
                    options: [
                        {
                            text: 'Private',
                            value: 'private',
                        },
                        {
                            text: 'Shared',
                            value: 'shared',
                        },
                    ],
                },
            ];
        default:
            return [];
    }
};

const METADATA_AIRBNB_KEYS_MAPPING = Object.freeze({
    //map lodgix amenities to airbnb allowed names
    bbq_area: 'bbq_grill',
    bed_linens: 'bedLinens',
    childrens_books_and_toys: 'booksAndToys',
    shampoo: 'essentials',
    refrigerator: 'fridge',
    jacuzzi: 'hot_tub',
    washer: 'laundry',
    dryer: 'laundry',
    beachfront: 'beach_access',
    wireless_internet: 'wifi',
    laptop_friendly_workspace: 'workspace',
    street_parking: 'parking',
    free_parking: 'parking',
    paid_parking: 'parking',
    paid_parking_on_premises: 'parking',
    patio_or_belcony: 'balcony',
    body_soap: 'essentials',
    conditioner: 'essentials',
    wardrobe_or_closet: 'clothing_storage',
});

const parseNestedObj = obj => {
    let newObj = {};
    for (let prop in obj) {
        if (Array.isArray(obj[prop])) {
            newObj = {
                name: prop,
                value: obj[prop],
            };
        }
        if (typeof Number(obj[prop]) === NUMBER_TYPE) {
            newObj = {
                name: prop,
                value: obj[prop],
            };
        }
        if (typeof obj[prop] === 'string') {
            newObj = {
                name: prop,
                value: obj[prop],
            };
        }
        if (isPlainObject(obj[prop])) {
            parseNestedObj(obj[prop]);
        }
    }

    return newObj;
};

const flatObj = {};

const getFlattenObj = (obj, newObj) => {
    //convert nested object to flat
    const keys = Object.keys(obj);
    keys.forEach(key => {
        if (isPlainObject(obj[key])) {
            getFlattenObj(obj[key], newObj);
        } else {
            newObj[key] = obj[key];
        }
    });
};

export const parseMetaData = (metaDataString, amenityKey) => {
    // parse metadata string to flat object
    const replaceString = metaDataString.replaceAll(`'`, `"`);
    const parsedJson = JSON.parse(replaceString);
    const metaDataOption = getAmenityAdditionalMetaDataOptions(amenityKey);
    const metaDataObj = {};
    const parsedObj = parseNestedObj(parsedJson);
    getFlattenObj(parsedObj.value, flatObj);
    metaDataOption.forEach(opt => {
        if (typeof flatObj === 'object') {
            const keys = Object.keys(flatObj);
            keys.forEach(key => {
                if (opt.key === key) {
                    metaDataObj[opt.type] = { ...metaDataObj[opt.type] };
                    metaDataObj[opt.type][opt.key] =
                        opt.type ===
                        (DATERANGE_TYPE || opt.type === TIMERANGE_TYPE)
                            ? flatObj[key][0]
                            : flatObj[key];
                }
            });
        } else {
            metaDataObj[opt.type] = flatObj;
        }
    });
    return metaDataObj;
};

const removeEmptyObjects = o => {
    //remove empty values from object
    if (isFunction(o) || !isPlainObject(o)) return o;

    if (isArray(o)) return o.map(removeEmptyObjects);

    return fromPairs(
        Object.entries(o)
            .map(([k, v]) => [k, removeEmptyObjects(v)])
            .filter(
                ([k, v]) =>
                    !(
                        v == null ||
                        v === '' ||
                        v.length === 0 ||
                        (isObject(v) && isEmpty(v))
                    )
            )
    );
};

export const generateRequestBody = (amenityObj, amenityList) => {
    // convert flat object to nested according keys,groups and parents
    const clonedAmenityObj = JSON.parse(JSON.stringify(amenityObj));
    const amenityId = clonedAmenityObj.amenity.split('-')[1];
    clonedAmenityObj.amenity = clonedAmenityObj.amenity.split('-')[0];
    const amenity = amenityList.find(amenity => {
        return (
            Number(amenity.id) === Number(amenityId) &&
            amenity.key_name === clonedAmenityObj.amenity
        );
    });
    const metaDataOption = getAmenityAdditionalMetaDataOptions(
        clonedAmenityObj.amenity
    );
    const metaDataObj = {};
    metaDataOption.forEach(opt => {
        let value = clonedAmenityObj.metaData[opt.type][opt.key];
        if (
            opt.type === DATERANGE_TYPE &&
            value &&
            value.start_date &&
            value.end_date
        ) {
            value = {
                start_date: value.start_date.replaceAll('-', ''),
                end_date: value.end_date.replaceAll('-', ''),
            };
        }
        if (
            opt.type === TIMERANGE_TYPE &&
            value &&
            value.start_time &&
            value.end_time
        ) {
            value = {
                start_time: value.start_time.replaceAll(':', '').slice(0, -2),
                end_time: value.end_time.replaceAll(':', '').slice(0, -2),
            };
        }
        if (opt.groupBy) {
            if (opt.parentGroup) {
                metaDataObj[opt.parentGroup] = {
                    ...metaDataObj[opt.parentGroup],
                    [opt.groupBy]: {
                        ...metaDataObj[opt.parentGroup][opt.groupBy],
                        [opt.key]: value,
                    },
                };
            } else {
                metaDataObj[opt.groupBy] =
                    opt.type === DATERANGE_TYPE || opt.type === TIMERANGE_TYPE
                        ? {
                              ...metaDataObj[opt.groupBy],
                              [opt.key]: [value],
                          }
                        : {
                              ...metaDataObj[opt.groupBy],
                              [opt.key]: value,
                          };
            }
        } else {
            metaDataObj[opt.key] =
                opt.type === DATERANGE_TYPE || opt.type === TIMERANGE_TYPE
                    ? [value]
                    : value;
        }
    });
    const mappedItem = {
        amenity_pk: amenity?.id,
        instructions: clonedAmenityObj.instructions,
        name: clonedAmenityObj.name,
    };
    if (clonedAmenityObj.amenity) {
        let metadataKey = clonedAmenityObj.amenity;
        if (metadataKey in METADATA_AIRBNB_KEYS_MAPPING) {
            metadataKey = METADATA_AIRBNB_KEYS_MAPPING[metadataKey];
        }
        const clearedMetaDataObj = removeEmptyObjects(metaDataObj);
        mappedItem['metadata'] = !isEmpty(clearedMetaDataObj)
            ? JSON.stringify({
                  [metadataKey]: clearedMetaDataObj,
              })
            : '';
    }
    return mappedItem;
};

export const parseMetaDataToHumanReadString = metaDataString => {
    const metaDataColumnObj = {};
    const replaceString = metaDataString.replaceAll(`'`, `"`);
    const parsedJson = JSON.parse(replaceString);
    const parsedObj = parseNestedObj(parsedJson);
    getFlattenObj(parsedObj.value, metaDataColumnObj);
    let string = '';
    Object.keys(metaDataColumnObj).forEach(key => {
        let title = key;
        let value = metaDataColumnObj[key];
        if (Array.isArray(value)) {
            value = value
                .map(word => {
                    return isPlainObject(word)
                        ? Object.values(word).join(',')
                        : word;
                })
                .join(', ');
        } else if (isPlainObject(value)) {
            value = Object.values(value).join(',');
        }
        string += `${title}: ${value} <br/>`;
    });
    return string;
};

export const generateAmenityMetadataName = (metadataObj, amenityName) => {
    const amenityKey = metadataObj.amenity.split('-').length
        ? metadataObj.amenity.split('-')[0]
        : '';
    const amenity = amenityName || amenityKey;
    const {
        text,
        number,
        select,
        multiselect,
        date_range,
    } = metadataObj.metaData;
    const getTitle = obj => {
        return Object.keys(obj)
            .map(key => `${obj[key]}`)
            .join(' ');
    };
    let valueString = '';
    if (!isEmpty(text)) {
        const textTitle = getTitle(text);
        valueString += ` ${textTitle}`;
    }
    if (!isEmpty(number)) {
        const numberTitle = getTitle(number);
        valueString += ` ${numberTitle}`;
    }
    if (!isEmpty(select)) {
        const selectTitle = getTitle(select);
        valueString += ` ${selectTitle}`;
    }
    if (!isEmpty(multiselect)) {
        const multiSelectTitle = getTitle(multiselect);
        valueString += ` ${multiSelectTitle}`;
    }
    return `${amenity} ${valueString}`.slice(0, 45);
};
